/**
 * Shared primitives for enterprise list URL state.
 * Modules keep their own filter schemas; this file only provides safe parse/serialize helpers.
 *
 * Precedence when URL sync is active (enforced by useEnterpriseListController):
 *   URL → primary source of truth
 *   sessionStorage → compatibility mirror only (Wave 5 may remove dual writes)
 */

/**
 * @param {string|URLSearchParams} search
 * @returns {string} normalized query string without leading `?`, keys sorted
 */
export function normalizeSearchString(search = "") {
  const raw = String(search || "").replace(/^\?/, "");
  if (!raw) return "";
  const params = new URLSearchParams(raw);
  const keys = [...new Set([...params.keys()])].sort();
  const out = new URLSearchParams();
  for (const key of keys) {
    for (const value of params.getAll(key)) {
      out.append(key, value);
    }
  }
  return out.toString();
}

/**
 * Stable cache / scroll key: pathname + normalized search.
 * @param {string} pathname
 * @param {string} [search]
 */
export function buildRouteStateKey(pathname, search = "") {
  const path = String(pathname || "/");
  const norm = normalizeSearchString(search);
  return norm ? `${path}?${norm}` : path;
}

/**
 * @param {unknown} value
 * @param {number} [fallback=1]
 */
export function parsePositiveInt(value, fallback = 1) {
  const n = parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(n) || n < 1) return fallback;
  return n;
}

/**
 * @param {unknown} value
 * @param {{ min?: number, max?: number, fallback: number }} opts
 */
export function parseClampedInt(value, { min = 1, max = 100, fallback }) {
  const n = parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

/**
 * @param {unknown} value
 * @param {'asc'|'desc'} [fallback='asc']
 * @returns {'asc'|'desc'}
 */
export function parseSortDir(value, fallback = "asc") {
  const v = String(value || "").toLowerCase();
  if (v === "asc" || v === "desc") return v;
  return fallback === "desc" ? "desc" : "asc";
}

/**
 * @template T
 * @param {unknown} value
 * @param {T[]} allowed
 * @param {T} fallback
 * @returns {T}
 */
export function parseEnum(value, allowed, fallback) {
  if (!Array.isArray(allowed) || allowed.length === 0) {
    return value != null && value !== "" ? /** @type {T} */ (value) : fallback;
  }
  return allowed.includes(/** @type {T} */ (value)) ? /** @type {T} */ (value) : fallback;
}

/**
 * @param {unknown} value
 * @param {boolean} [fallback=false]
 */
export function parseBooleanFlag(value, fallback = false) {
  if (value == null || value === "") return fallback;
  const v = String(value).toLowerCase();
  if (v === "1" || v === "true" || v === "yes") return true;
  if (v === "0" || v === "false" || v === "no") return false;
  return fallback;
}

/**
 * Serialize list state into URLSearchParams, omitting defaults.
 *
 * @param {object} state
 * @param {string} [state.q]
 * @param {Record<string, string|null|undefined>} [state.filters]
 * @param {{ key: string, dir: string }} [state.sort]
 * @param {{ page?: number, pageSize?: number }} [state.pagination]
 * @param {object} defaults
 * @param {string} [defaults.sortKey]
 * @param {string} [defaults.sortDir]
 * @param {number} [defaults.page=1]
 * @param {number} [defaults.pageSize]
 * @param {Record<string, string>} [defaults.filters]
 * @returns {URLSearchParams}
 */
export function serializeListSearchParams(state = {}, defaults = {}) {
  const params = new URLSearchParams();
  const q = String(state.q || "").trim();
  if (q) params.set("q", q);

  const filters = state.filters || {};
  const defaultFilters = defaults.filters || {};
  for (const [key, raw] of Object.entries(filters)) {
    if (raw == null || raw === "" || raw === "__all__") continue;
    const def = defaultFilters[key];
    if (def != null && String(raw) === String(def)) continue;
    params.set(`filter_${key}`, String(raw));
  }

  const sortKey = state.sort?.key;
  const sortDir = state.sort?.dir;
  if (sortKey && sortKey !== defaults.sortKey) {
    params.set("sort", String(sortKey));
  } else if (sortKey && sortDir && sortDir !== defaults.sortDir) {
    // Keep sort key if only dir differs from default (stable shareable URL)
    params.set("sort", String(sortKey));
  }
  if (sortDir && sortDir !== defaults.sortDir) {
    params.set("dir", parseSortDir(sortDir, defaults.sortDir || "asc"));
  } else if (sortKey && sortKey !== defaults.sortKey && sortDir) {
    params.set("dir", parseSortDir(sortDir, defaults.sortDir || "asc"));
  }

  const page = Number(state.pagination?.page) || 1;
  const pageSize = Number(state.pagination?.pageSize);
  if (page > (defaults.page || 1)) params.set("page", String(page));
  if (Number.isFinite(pageSize) && defaults.pageSize != null && pageSize !== defaults.pageSize) {
    params.set("pageSize", String(pageSize));
  }

  return params;
}

/**
 * @param {string|URLSearchParams} search
 * @param {object} schema
 * @returns {{ q: string, filters: Record<string, string>, sort: { key: string, dir: 'asc'|'desc' }, pagination: { page: number, pageSize: number } }}
 */
export function parseListSearchParams(search, schema = {}) {
  const params = typeof search === "string" || search == null
    ? new URLSearchParams(String(search || "").replace(/^\?/, ""))
    : search;

  const defaultFilters = schema.defaultFilters || {};
  const allowedSortKeys = schema.allowedSortKeys || [];
  const defaultSortKey = schema.defaultSortKey || "id";
  const defaultSortDir = schema.defaultSortDir || "desc";
  const defaultPageSize = schema.defaultPageSize || 25;
  const minPageSize = schema.minPageSize || 1;
  const maxPageSize = schema.maxPageSize || 100;

  const q = params.get("q") || "";

  const filters = {};
  for (const key of Object.keys(defaultFilters)) {
    const fromUrl = params.get(`filter_${key}`);
    filters[key] = fromUrl != null && fromUrl !== "" ? fromUrl : defaultFilters[key];
  }

  const sortKey = parseEnum(params.get("sort") || defaultSortKey, allowedSortKeys.length ? allowedSortKeys : null, defaultSortKey);
  const sortDir = parseSortDir(params.get("dir"), defaultSortDir);

  const page = parsePositiveInt(params.get("page"), 1);
  const pageSize = parseClampedInt(params.get("pageSize"), {
    min: minPageSize,
    max: maxPageSize,
    fallback: defaultPageSize,
  });

  return {
    q,
    filters,
    sort: { key: sortKey, dir: sortDir },
    pagination: { page, pageSize },
  };
}

/**
 * Build a stable list query cache key.
 * @param {string} domain e.g. "service" | "kunden" | "projekt:elektro"
 * @param {object} params normalized request params
 */
export function buildListCacheKey(domain, params = {}) {
  const normalized = {
    q: String(params.q || "").trim(),
    filters: params.filters || {},
    sort: params.sort || {},
    page: Number(params.page) || 1,
    pageSize: Number(params.pageSize) || 0,
  };
  // Stable JSON: sort filter keys
  const filterKeys = Object.keys(normalized.filters).sort();
  const filters = {};
  for (const k of filterKeys) filters[k] = normalized.filters[k];
  return `${domain}:list:${JSON.stringify({
    q: normalized.q,
    filters,
    sort: { key: normalized.sort.key || "", dir: normalized.sort.dir || "" },
    page: normalized.page,
    pageSize: normalized.pageSize,
  })}`;
}
